/**
 * A small read-only chart with one pin.
 *
 * Exists for one job: letting a captain confirm a position they did not place
 * by hand. A coordinate typed into two fields, or read off a photograph by OCR,
 * is a number until it is a place — and a transposed digit that puts the pin in
 * the next fjord is obvious on a chart and invisible in text.
 *
 * Deliberately not MapView. That one carries the layer switcher, clustering,
 * the long-press gesture and four data layers, none of which belong in a 180px
 * confirmation box. Kartverket only, no interaction beyond pinch-zoom.
 */

import 'leaflet/dist/leaflet.css'
import L from 'leaflet'
import { useEffect, useRef } from 'react'

export function PositionPreview({
  lat,
  lon,
  zoom = 12,
  height = 180,
  compare,
}: {
  lat: number
  lon: number
  zoom?: number
  height?: number
  /**
   * A second position to show alongside the first, with a line between them.
   * Used for CSC's counter-proposal: the captain is deciding between two
   * places, and two numbers on a screen do not answer "is that inside the
   * shelter or outside it" -- the chart does.
   *
   * When set, the map fits both pins instead of centring on one, so `zoom` is
   * ignored.
   */
  compare?: { lat: number; lon: number }
}) {
  const hostRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<L.Map | null>(null)
  const markerRef = useRef<L.CircleMarker | null>(null)
  const compareRef = useRef<L.CircleMarker | null>(null)
  const linkRef = useRef<L.Polyline | null>(null)

  useEffect(() => {
    if (!hostRef.current || mapRef.current) return
    const map = L.map(hostRef.current, {
      center: [lat, lon],
      zoom,
      attributionControl: false,
      zoomControl: false,
      // No dragging: this is a confirmation, not a picker. Panning away from
      // the pin would defeat the point of showing it.
      dragging: false,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      keyboard: false,
    })
    L.tileLayer(
      'https://cache.kartverket.no/v1/service?layer=sjokartraster&style=default' +
        '&tilematrixset=webmercator&Service=WMTS&Request=GetTile&Version=1.0.0' +
        '&Format=image/png&TileMatrix={z}&TileCol={x}&TileRow={y}',
      { maxZoom: 18 },
    ).addTo(map)
    L.control.scale({ imperial: false, position: 'bottomleft' }).addTo(map)
    markerRef.current = L.circleMarker([lat, lon], {
      radius: 10,
      weight: 3,
      color: 'var(--abg-navy)',
      fillColor: 'var(--abg-band)',
      fillOpacity: 0.8,
    }).addTo(map)
    mapRef.current = map
    return () => {
      map.remove()
      mapRef.current = null
      markerRef.current = null
      compareRef.current = null
      linkRef.current = null
    }
    // Mount once; the effect below follows subsequent position changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    markerRef.current?.setLatLng([lat, lon])

    if (!compare) {
      // Tear down the comparison if it goes away -- e.g. the captain accepted
      // and the same component is reused for the new application.
      compareRef.current?.remove()
      linkRef.current?.remove()
      compareRef.current = null
      linkRef.current = null
      map.setView([lat, lon], zoom)
      return
    }

    const here: L.LatLngTuple = [lat, lon]
    const there: L.LatLngTuple = [compare.lat, compare.lon]

    if (!linkRef.current) {
      linkRef.current = L.polyline([here, there], {
        color: 'var(--abg-navy)',
        weight: 2,
        dashArray: '5 4',
        opacity: 0.7,
      }).addTo(map)
    } else {
      linkRef.current.setLatLngs([here, there])
    }

    if (!compareRef.current) {
      // Hollow, so the two pins are told apart by shape rather than colour
      // alone -- this is a decision screen, and roughly one man in twelve
      // cannot rely on the difference between amber and navy.
      compareRef.current = L.circleMarker(there, {
        radius: 10,
        weight: 3,
        color: 'var(--flag-amber)',
        fillColor: 'transparent',
        fillOpacity: 0,
      }).addTo(map)
    } else {
      compareRef.current.setLatLng(there)
    }

    // Fit both, with enough padding that neither pin sits on the frame. On two
    // positions a few hundred metres apart Leaflet would otherwise pick a zoom
    // where the pins touch the edges and the surrounding water -- the thing
    // being judged -- is cropped away.
    map.fitBounds(L.latLngBounds([here, there]), { padding: [36, 36], maxZoom: 16 })
  }, [lat, lon, zoom, compare?.lat, compare?.lon])

  return (
    <div
      ref={hostRef}
      style={{ height, width: '100%' }}
      aria-label={compare ? 'Both positions on chart' : 'Position on chart'}
    />
  )
}
